/**
 * Alarm sound runner. Watches the live safety-alarm state and drives the audible
 * siren. While any alarm is standing it works out the highest-priority one and
 * plays THAT alarm's chosen tone at its chosen volume, repeating until the last
 * alarm clears. So an anchor drag and a low battery don't sound the same, and if
 * a more dangerous alarm joins in, the tone swaps up to it. This is the
 * foreground (app on screen) audible path; startAlarmSound/stopAlarmSound are
 * idempotent for the same tone, so a re-render while an alarm holds neither
 * stacks timers nor re-triggers the burst.
 *
 * It sits alongside HapticsRunner, which buzzes on the rising edge. Together an
 * on-screen alarm now banners, buzzes, AND sounds. When the phone is locked or
 * the app is backgrounded the WebView is frozen so none of these can run; the
 * Android local notification (its own alarm channel, loud sound) covers that.
 */
import { useEffect } from 'react';
import { useAlarmsStore } from '@/store/alarms';
import { useAlarmSoundsStore } from '@/store/alarmSounds';
import { startAlarmSound, stopAlarmSound } from '@/ui/alarmSound';
import { audibleAlarmKey } from '@/safety/alarmState';

export function AlarmSoundRunner() {
  const live = useAlarmsStore((s) => s.live);
  const masterMuted = useAlarmsStore((s) => s.masterMuted);
  const acked = useAlarmsStore((s) => s.acked);
  const soundConfig = useAlarmSoundsStore((s) => s.config);

  useEffect(() => {
    // audibleAlarmKey folds master mute in: muted → null → the siren stays off
    // even while the alarm still stands (and still banners). Flipping mute back
    // on re-runs this effect and the siren picks straight back up.
    const key = audibleAlarmKey(live, masterMuted, acked);
    if (key) {
      const chosen = soundConfig[key];
      startAlarmSound({ tone: chosen.tone, volume: chosen.volume });
    } else {
      stopAlarmSound();
    }
  }, [live, soundConfig, masterMuted, acked]);

  // Stop the siren if this ever unmounts (app teardown), so nothing is left
  // beeping without a runner to silence it.
  useEffect(() => stopAlarmSound, []);

  return null;
}
